<script setup>
import {ErrorMessage, Field, Form} from 'vee-validate'
import {computed, reactive, ref} from 'vue'
import axios from 'axios'
import {useFormSubmit} from '../../composables/useFormSubmit'

const { showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()
const props = defineProps({
  postUrl: {
    type: String,
    default: '',
  },
  textareaPlaceholder: {
    type: String,
    default: '',
  },
  listOfLevelOfCare: {
    type: Array,
    default: () => [],
  },
})

const validationSchema = computed(() => {
  let schema = {
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    phone_number: 'required|phoneNumber',
  }

  if (!showAskQuestionForm.value) {
    schema.levelOfCare = 'required'
  }

  return schema
})

let isSubmitting = ref(false)
const confirmation = reactive({show: false, message: '', class: ''})
const showAskQuestionForm = ref(false)

const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('careHomeContactForm');
  let formData = new FormData(form);
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function (response) {
      confirmation.class = 'message--success'
      confirmation.message = response.data.msg
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      let currentUrl = window.location.href;
      currentUrl = currentUrl.replace('#enquiry-submitted', '');
      history.pushState(null, null, `${currentUrl}#enquiry-submitted`);

      setTimeout(function () {
        confirmation.show = false;
      }, 4000);
    }).catch(error => {
      submitButton.disabled = false;
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}
const toggleCareHomeType = (action) => {
  showAskQuestionForm.value = action !== 'availability';
}

</script>

<template>
  <Form
    :validation-schema="validationSchema"
    v-slot="{ errors }"
    @submit="onSubmit"
    id="careHomeContactForm"
  >
    <slot name="token"></slot>
    <div class="village__form">
      <div class="row d-none d-md-block">
        <div class="col-lg-12 pt-4 d-flex align-items-lg-center flex-column flex-lg-row">
          <label class="d-inline-block mr-2 mb-5 mb-lg-3"><strong>I would like to:</strong></label>
          <div class="radio-custom radio-custom--inline radio-custom--white">
            <input type="radio" id="check-availability" name="subject" value="Check availability" checked @change="toggleCareHomeType('availability')">
            <label class="form-check-label" for="check-availability">Check availability</label>
          </div>
          <div class="radio-custom radio-custom--inline radio-custom--white">
            <input type="radio" id="ask-question" name="subject" value="Ask question detail" @change="toggleCareHomeType('ask')">
            <label class="form-check-label" for="ask-question">Ask a question</label>
          </div>
          <a href="/rest-home/aged-care-employment" class="btn btn--job-enquiries mb-0 ml-lg-auto">For job enquiries <span>click here</span></a>
        </div>
      </div>
      <div class="row mt-4 mt-md-8">
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="first_name">First name <span class="text-red">*</span></label>
            <Field
              id="first_name"
              name="first_name"
              class="form-control"
              :class="{'is-danger': errors['first_name'] }"
            />
            <ErrorMessage name="first_name" class="is-danger" />
          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="last_name">Last name <span class="text-red">*</span></label>
            <Field
              name="last_name"
              class="form-control"
              :class="{'is-danger': errors['last_name'] }"
              id="last_name"
            />
            <ErrorMessage name="last_name" class="is-danger" />
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="email" class="font-weight-light-bold">Email <span class="text-red">*</span></label>
            <Field
              id="email"
              name="email"
              class="form-control"
              :class="{'is-danger': errors['email'] }"
            />
            <ErrorMessage name="email" class="is-danger" />
          </div>
        </div>
        <div class="col-lg-6">
          <div class="form-group form-group--input">
            <label for="phone_number">Phone number <span class="text-red">*</span></label>
            <Field
              id="phone_number"
              name="phone_number"
              class="form-control"
              :class="{'is-danger': errors['phone_number'] }"
            />
            <ErrorMessage name="phone_number" class="is-danger" />
          </div>
        </div>
      </div>

      <div v-if="!showAskQuestionForm">
        <div class="row mt-4">
          <div class="col-lg-6">
            <div class="form-group form-group--input">
              <p class="fs-16 font-weight-bold color-pickled mt-0">
                Has a needs assessment been completed? <span class="text-red">*</span>
              </p>
              <div class="radio-custom radio-custom--inline radio-custom--white">
                <input type="radio" id="yes" name="assessment" value="Yes" checked>
                <label class="form-check-label fs-16 font-weight-light-bold" for="yes">Yes</label>
              </div>
              <div class="radio-custom radio-custom--inline radio-custom--white">
                <input type="radio" id="in_progress" name="assessment" value="in progress">
                <label class="form-check-label fs-16 font-weight-light-bold" for="in_progress">It's in progress</label>
              </div>
              <div class="radio-custom radio-custom--inline radio-custom--white">
                <input type="radio" id="No" name="assessment" value="No">
                <label class="form-check-label fs-16 font-weight-light-bold" for="No">No</label>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-group form-group--input">
              <label class="fs-16 font-weight-bold color-pickled d-block">
                What level of care is needed? <span class="text-red">*</span>
              </label>
              <Field
                name="levelOfCare"
                as="select"
                class="form-control form-control--select form-control--select-45 color-pickled"
                aria-required="true"
                aria-invalid="false"
              >
                <option :value="item.value" v-for="item in props.listOfLevelOfCare" :key="item.name" v-html="item.name"></option>
              </Field>
              <ErrorMessage name="levelOfCare" class="is-danger" as="span">
                The level of care field is required
              </ErrorMessage>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="row mt-4">
          <div class="col-lg-12">
            <div class="form-group form-group--input">
              <label for="comment">Message</label>
              <textarea
                id="message"
                rows="6"
                class="form-control"
                ref="contactFormMessage"
                name="message"
                placeholder=""
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <input type="hidden" name="captcha_action" value="care_home_enquiry">
      <transition name="fade">
        <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
      </transition>
      <div class="row">
        <div class="col-lg-12 text-center">
          <slot name="hiddenfields" v-if="!showAskQuestionForm"></slot>
          <slot name="askhiddenfields" v-if="showAskQuestionForm"></slot>
          <input v-if="showAskQuestionForm" type="submit" value="Send Message" class="btn btn--green btn--large submit mb-10">
          <input
            v-if="!showAskQuestionForm"
            type="submit"
            value="Check room availability"
            class="button button--green button--h-45 fs-14 pl-15 pr-15 w-auto submit mt-6 mb-10 ml-auto mr-auto"
          >
        </div>
      </div>
    </div>
  </Form>
</template>
